"use client";

import dynamic from "next/dynamic";
import { useScrollDirection } from "@hooks/useScrollDirection";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import { Box } from "@ecco/ui/box";
import { useXlBreakpoint } from "@ecco/ui/use-breakpoint";
import { sizes } from "@ecco/ui/tokens";

const ProductChat = dynamic(() => import("./ProductChat").then((module) => module.ProductChat), {
    ssr: false,
});

const BOTTOM_BAR_SCROLL_THRESHOLD = 100;

type AiChatProps = {
    placement: "navigation" | "bottom-bar";
};

const NavigationAiChat = () => {
    const isDesktop = useXlBreakpoint();

    return isDesktop ? <ProductChat triggerVariant="navigation" /> : null;
};

const BottomBarAiChat = () => {
    const isDesktop = useXlBreakpoint();
    const { scrollDirection } = useScrollDirection({ deadZone: BOTTOM_BAR_SCROLL_THRESHOLD });

    if (isDesktop) {
        return null;
    }

    const isVisible = scrollDirection === "down";

    return (
        <Box
            data-testid="AiChatBottomBar"
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            h={sizes.navBarCollapsedHeight}
            zIndex={8}
            bg="white"
            borderTopWidth="1px"
            borderTopColor="gray.200"
            pb="env(safe-area-inset-bottom)"
            transform={isVisible ? "translateY(0)" : "translateY(100%)"}
            visibility={isVisible ? "visible" : "hidden"}
            transition="transform 0.3s ease-in-out, visibility 0.3s ease-in-out"
        >
            <ProductChat triggerVariant="bottom-bar" />
        </Box>
    );
};

export const AiChat = ({ placement }: AiChatProps) => {
    const isAiChatEnabled = process.env.NEXT_PUBLIC_IS_AI_CHAT_ENABLED === "true";

    if (!isAiChatEnabled) {
        return null;
    }

    return placement === "navigation" ? <NavigationAiChat /> : <BottomBarAiChat />;
};
